import {VisualizationUpdateLevel, Visualizer, VisualizerControl} from "../data-source/visualizer";
import {Timeline} from "../timeline/timeline";
import {
    MapDataCluster,
    MapLayout,
    MapOverlay,
    MapOverlayCategory,
    MapOverlayDatumContentProducer
} from "../map/map-overlay";
import {DataSource} from "../data-source/source";
import {ObservationTypeDefinition} from "./container-observation";
import {measureText} from "../util/dom-util";
import {toRadians, UTM} from "../map/projection";
import {Coordinate, LatLng, Pixel} from "../map/map-types";
import {Path} from "../svg/path";
import {ObservationDatum} from "./observation";


export type ArcOfInfluenceConfiguration = {
    opts?: ArcOfInfluenceVisualOptions; // drawing related settings
}

type ArcOfInfluenceVisualOptions = {
    stroke: string; // outer lines and dashed center line
    fill: string; // inner arc
    length: number; // 'size' in meters
    strokeWidth: number;
    lowerWindSpeedThreshold: number; // don't draw an arc (but a dashed circle instead), when wind speed gets too low
}

export class ObservationsAoiVisualizer extends MapOverlay implements Visualizer {

    // a flag within an observation item indicating whether that item should draw the arc.
    // This flag is true for the single selection (if any) and is reset for any other item every time the selection changes
    private static readonly DrawArcFlag: string = 'DrawArc';
    private controls: VisualizerControl;
    private readonly obsTypeId: string;
    private config: ArcOfInfluenceConfiguration;

    constructor(private visualizerKey: string,
                private source: DataSource,
                observationTypeDefinition: ObservationTypeDefinition) {

        super(
            MapOverlayCategory.OBSERVATION_AOI,
            'observation-aoi-layer',
            {
                elementData: {
                    'source': source.getName(),
                    'visualizer': visualizerKey
                }
            },
            new MapOverlayDatumContentProducer(
                {
                    size: () => {
                        // needs to match observation map circles in size
                        return {width: 15, height: 15, border: 2};
                    },
                    draw: (domElem, meta, _, __) => {

                        // allow user to click 'through' the arc area
                        domElem.style.pointerEvents = 'none';
                        // the normal selection mechanism would give the empty div a box shadow
                        domElem.style.boxShadow = 'none';

                        // always remove a potential previous SVG element regardless of what we do later
                        let svgChild = domElem.querySelector('svg.aoiArc');
                        if (svgChild) {
                            svgChild.remove();
                        }

                        // check that whatever we're currently drawing has exactly one data item which has the
                        // 'draw an arc' flag set. Otherwise, we're either in a merged cluster, do not have weather
                        // data, or we're not supposed to draw anything
                        const clusterItemsWithWeatherData =
                            meta
                                .dataItemIds
                                .map((itemId: string) => meta.dataItems[itemId])
                                .filter((dataItem: ObservationDatum) =>
                                    ObservationsAoiVisualizer.DrawArcFlag in dataItem &&
                                    // could still contain just null values
                                    dataItem.weatherData);

                        if (clusterItemsWithWeatherData.length == 1) {
                            this.drawArc(domElem, clusterItemsWithWeatherData[0])
                        }
                    },
                    overlap: (_, __) => false,
                    merge: (_, __): MapDataCluster => undefined
                },
                source,
                {
                    layout: MapLayout.overlap
                }, 'aoi')
        ); // end-of super ctor call

        this.obsTypeId = observationTypeDefinition.id;
        this.config = observationTypeDefinition.features.arcOfInfluence;

    }

    key = (): string => this.visualizerKey;
    type = (): string => this.obsTypeId;

    get isActive(): boolean {
        return this.controls?.enabled(this.key()) ?? false;
    }

    setControls = (controls: { [key: string]: VisualizerControl; }) => {
        if (controls) {
            this.controls = controls[this.type()];
        }
        return this;
    };

    setTimeline = (_: Timeline) => {
    };

    refresh = (updateLevel: VisualizationUpdateLevel) => {
        if (this.controls) {
            // somewhat copy and paste from map viewer...
            // we detect an on/off event by simply checking if the settings changed, since
            // we only have one setting/checkbox at all
            if (this.isActive && updateLevel === VisualizationUpdateLevel.settings) {
                // NB: order matters, set map first, then data
                this.setMap(this.source.getMap());
                let dataMap = this.source.getData([this.obsTypeId]);
                (<MapOverlayDatumContentProducer>this.getProducer()).setData(dataMap);
                void this.updateSelection();
            } else if (!this.isActive && updateLevel === VisualizationUpdateLevel.settings) {
                (this.getProducer() as MapOverlayDatumContentProducer).setData({});
                this.setMap(undefined);
            }
        }
    };

    updateSelection = async () => {

        if (!this.isActive) {
            return;
        }

        const data = this.source.getData()[this.type()];
        // reset draw arc flag for everyone
        data.getData().forEach(d => delete (d as any)[ObservationsAoiVisualizer.DrawArcFlag]);
        if (data?.getSelection().length === 1 && (data.getSelection()[0] as ObservationDatum).weatherData) {
            (data.getSelection()[0] as any)[ObservationsAoiVisualizer.DrawArcFlag] = true;
            this.update();
        } else if (data?.getSelection().length === 0) {
            this.update();
        }

    };

    private drawArc(domElem: HTMLElement, dataItem: ObservationDatum): void {
        const w: number = domElem.offsetWidth;
        const h: number = domElem.offsetHeight;

        // center of our div container, which is the same as the center of the circle produced by the default map viewer
        // which acts as the 'visual center' to the user
        const center = {
            x: domElem.offsetWidth / 2 - 2, // account for border
            y: domElem.offsetHeight / 2 - 2
        }

        if (!dataItem.weatherData['ws'] || !dataItem.weatherData['wd']) {
            return;
        }

        const wd = dataItem.weatherData.wd;
        const ws = dataItem.weatherData.ws;

        if (!ws || !wd || Number.isNaN(wd) || wd < 0.0 || wd > 360.0) {
            return;
        }

        // grab drawing options or use defaults

        const styleOpts: ArcOfInfluenceVisualOptions = this.config?.opts;

        styleOpts.stroke = styleOpts.stroke || domElem.style.backgroundColor;
        styleOpts.fill = styleOpts.fill || 'green';
        styleOpts.strokeWidth = styleOpts.strokeWidth || 2;
        styleOpts.lowerWindSpeedThreshold = styleOpts.lowerWindSpeedThreshold || 2;
        styleOpts.length = styleOpts.length || 1000;

        let svgContent: string;

        if (ws < styleOpts.lowerWindSpeedThreshold) { // no enough wind, dashed circle
            svgContent = `<circle cx='${center.x}' cy='${center.y}' r='40' fill='none' stroke='${styleOpts.stroke}' stroke-dasharray='6'
                                                     stroke-width='${styleOpts.strokeWidth}'/>`
        } else {

            const windDirectionAngle = (wd + 90) % 360;
            const arcSizeDegrees = 60;
            const {lat, lng} = {lat: dataItem.geometry.lat, lng: dataItem.geometry.lng};
            // need to undo the translation of the div itself
            const domElemOffset = {
                x: -parseInt(domElem.style.left.replace('px', '')),
                y: -parseInt(domElem.style.top.replace('py', ''))
            }

            const arcPainter = new AoiPainter(
                wd,
                arcSizeDegrees,
                dataItem.id,
                this.getProjection().fromLatLngToPixel,
                {lat, lng},
                {
                    x: domElemOffset.x,
                    y: domElemOffset.y,
                    w,
                    h
                },
                styleOpts
            );

            const lines = arcPainter.getMainArc(true);

            if (!lines) { // too short
                return;
            }

            // the wind indicator is not part of the arc painter class since it is
            // specific to the observation variant
            const indicatorHalfSize = 15; // radius circle with n,e,s,w gaps
            const cutOutSize = 2; // half-size of these gaps
            const arrowOffset = 8; // offset wind direction arrow within circle
            const arrowColor = "rgb(86,147,200)";

            const fontSizePixels = 14;
            const text = `${ws.toFixed(1)} m/s`;
            const textWidth = measureText.getWidth(text, fontSizePixels);

            const textPadding = 10;
            const textBoxWidth = textWidth + textPadding * 2;

            // the indicator text box is translated to either the left or right side of the wind indicator
            // to ensure it does not cover the arc itself
            const textBoxTranslation = (arcPainter.heading >= 90 && arcPainter.heading <= 270)
                ? -(indicatorHalfSize + textBoxWidth + 10)
                : indicatorHalfSize + 10;

            // the whole indicator (pointer arrow and wind speed box) gets placed on a circle
            // around the observation opposite to the arc
            const indicatorTranslation = arcPainter.pointOnCircle(toRadians(windDirectionAngle), center.x, center.y, 40);

            const windDirIndicator =
                `<g transform="translate(${indicatorTranslation[0]},${indicatorTranslation[1]})" >
                    <circle stroke-width="1.5" cx="0" cy="0" r="${indicatorHalfSize}" stroke="black" fill="white"/>                                        
                    <g transform="translate(${textBoxTranslation},${(-(fontSizePixels + textPadding)) / 2})">
                        <rect fill="${arrowColor}" stroke="black" stroke-width="1.5" rx="8" rx="8" height="${fontSizePixels + textPadding}" width="${textBoxWidth}"/>
                        <text font-size="${fontSizePixels}" y="${fontSizePixels}" 
                         dominant-baseline="middle" alignment-baseline="middle"
                         x="${textBoxWidth / 2 - textWidth / 2}" stroke="none" fill="white">${text}</text>                                                
                    </g>                   
                    <rect fill="white" stroke="white" x="${-indicatorHalfSize}" y="${-cutOutSize}" width="${indicatorHalfSize * 2}" height="${cutOutSize * 2}"/>
                    <rect fill="white" stroke="white" y="${-indicatorHalfSize}" x="${-cutOutSize}" width="${cutOutSize * 2}" height="${indicatorHalfSize * 2}"/>
                    <path transform="rotate(${windDirectionAngle + 180}), translate(${-indicatorHalfSize}, ${-indicatorHalfSize})" fill="${arrowColor}" stroke="none" d="M ${arrowOffset / 2} ${indicatorHalfSize} L ${9 * indicatorHalfSize / 6} ${arrowOffset} 
                                            L ${9 * indicatorHalfSize / 6} ${2 * indicatorHalfSize - arrowOffset} z"/>
                    </g>`;

            svgContent = `<g stroke-width='${styleOpts.strokeWidth}' stroke='${styleOpts.stroke}'>` + lines + '</g>' + windDirIndicator;
            svgContent += arcPainter.getSvgDefsBlock({
                cx: w / 2,
                cy: w / 2,
                radius: w,
            });
        }

        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.style.overflow = 'visible';
        svg.style.pointerEvents = 'none'; // allow to click through
        svg.classList.add('aoiArc');
        svg.setAttribute('width', `${w}`);
        svg.setAttribute('height', `${h}`);
        svg.innerHTML = svgContent
        domElem.append(svg);
    }

}

/// a collection of methods for easier reuse from the legacy sensor module
export class AoiPainter {

    private readonly gradientId: string;
    private readonly maskId: string;
    private readonly utm: UTM;
    // arc main lines, center, left, right
    readonly heading: number;
    private readonly headingLeft: number;
    private readonly headingRight: number;

    constructor(
        wd: number, // unaltered wind speed as per sensor data
        arcSize: number, // size in degrees
        id: string, // unique data cluster or item id
        private fromLatLngToPixel: (pos: LatLng) => Pixel,
        private position: { lat: number, lng: number }, // data item position
        private domElement: { x: number, y: number, w: number, h: number }, // parent div
        private style: { stroke: string, strokeWidth: number, fill: string, length: number }
    ) {

        const windDirectionAngle = (wd + 90) % 360;

        // gradient for arc fill and cut out mask (the inner circle) is done through clip paths
        // and masks referred to by id. These are identical for all arcs within the same visualizer
        // but since only one arc is active at a time, not worth sharing
        this.gradientId = `${id}_fillgradient`;
        this.maskId = `${id}_mask`;

        const zone = UTM.getZone(position.lat, position.lng);
        const hemisphere = UTM.getHemisphere(position.lat, position.lng);
        this.utm = UTM.get(zone, hemisphere);

        this.heading = this.ensurePositiveAngle(windDirectionAngle);
        this.headingLeft = this.ensurePositiveAngle(windDirectionAngle - arcSize);
        this.headingRight = this.ensurePositiveAngle(windDirectionAngle + arcSize);
    }

    // returns the main arc part or null if it would be too short and should not be drawn at all
    getMainArc = (useMask: boolean = false): string => {
        const originMeters = this.utm.toCartesian(this.position.lat, this.position.lng);
        // arc lengths meters into either direction
        const leftArcTipMeters = this.pointOnCircle(toRadians(this.headingLeft), originMeters[0], originMeters[1], this.style.length);
        const rightArcTipMeters = this.pointOnCircle(toRadians(this.headingRight), originMeters[0], originMeters[1], this.style.length);
        // convert all of that back to pixels
        const originPixels = this.metersToDivPixel(originMeters);
        const leftTipPixels = this.metersToDivPixel(leftArcTipMeters);
        const rightTipPixels = this.metersToDivPixel(rightArcTipMeters);
        const centerLineMeters = this.pointOnCircle(toRadians(this.heading), originMeters[0], originMeters[1], this.style.length);
        const centerLinePixels = this.metersToDivPixel(centerLineMeters);

        // figure out how long in pixels the center line would be, if it is too short, don't continue at all
        const dirVec = {x: centerLinePixels.x - originPixels.x, y: centerLinePixels.y - originPixels.y};
        if (Math.sqrt(dirVec.x * dirVec.x + dirVec.y * dirVec.y) <= 20) {
            return;
        }

        // grab two additional, suitable controls points to ensure the arc curve hits the center line and
        // still looks smooth enough. These lines 'walk' from the tips left/right inwards
        const controlPointsOffset = 0.25; // percentage of total arc lengths
        const controlLeftPixels = this.metersToDivPixel(this.pointOnCircle(toRadians(this.headingLeft + 90), leftArcTipMeters[0], leftArcTipMeters[1], this.style.length * controlPointsOffset));
        const controlRightPixels = this.metersToDivPixel(this.pointOnCircle(toRadians(this.headingRight - 90), rightArcTipMeters[0], rightArcTipMeters[1], this.style.length * controlPointsOffset));

        let arcData = Path.smoothenLineString([
            leftTipPixels,
            controlLeftPixels,
            centerLinePixels,
            controlRightPixels,
            rightTipPixels
        ].map(p => [p.x, p.y])).flatMap(t => t).join(' ');
        let lines = `<path stroke="none" fill="url(#${this.gradientId})" d="${arcData} L ${originPixels.x} ${originPixels.y} z"/>`;
        lines += this.makeLine(originPixels, leftTipPixels) + this.makeLine(originPixels, rightTipPixels)
            + this.makeLine(originPixels, centerLinePixels, 'stroke-dasharray="6"');

        if (useMask) {
            lines = `<g mask="url(#${this.maskId})">${lines}</g>`;
        }

        return lines;
    }

    pointOnCircle = (a: number, ccx: number, ccy: number, r: number): Coordinate => [Math.cos(a) * r + ccx, Math.sin(a) * r + ccy];

    getSvgDefsBlock = (maskCenter: { cx: number, cy: number, radius: number } | undefined = null): string => {

        let defs = `<defs><linearGradient id='${this.gradientId}' x1="0" y1="0" x2="0" y2="1" gradientTransform="rotate(${this.heading - 90},0.5, 0.5)">` +
            `<stop offset='0%' stop-color="${this.style.fill}" stop-opacity='0'/>` +
            `<stop offset='50%' stop-color="${this.style.fill}" stop-opacity='.3'/>` +
            `<stop offset='100%' stop-color="${this.style.fill}" stop-opacity='1'/></linearGradient>`;

        if (maskCenter) {
            let mask = `<mask id="${this.maskId}"><rect x="-1000" y="-1000" width="2000" height="2000" fill="white"/><circle fill="black" 
                    cx="${maskCenter.cx}" cy="${maskCenter.cy}" r="${maskCenter.radius}"/></mask>`;

            defs += mask;
        }
        return defs + "</defs>";
    }

    private ensurePositiveAngle = (a: number) => (a + 360) % 360;
    private makeLine = (lp0: { x: number, y: number }, lp1: { x: number, y: number }, s: string = null) => {
        let lineMarkup = `<line x1='${lp0.x}' y1='${lp0.y}' x2='${lp1.x}' y2='${lp1.y}'`;
        return (s
            ? lineMarkup + s
            : lineMarkup) + "/>";
    };

    private metersToDivPixel = (xy: Coordinate): Pixel => {
        const px = this.fromLatLngToPixel(this.utm.toGeodetic(xy));
        return {
            x: -1 * (px.x + this.domElement.x) + this.domElement.w,
            y: (px.y + this.domElement.y)
        }
    }

}